import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Download, Loader2, Printer, ReceiptText, Search, Store, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ChainPayModal } from '@/components/invoices/ChainPayModal';
import { cn } from '@/lib/utils';
import BalanceWithCredits from '@/components/billing/BalanceWithCredits';
import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import { fetchFunctionPdf, runPdf } from '@/lib/pdf';
import { useGroupInvoices, type GroupInvoiceRow } from '@/hooks/useGroupInvoices';

/**
 * /invoices for a chain's ACCOUNTING login — every store's invoices in one
 * table, with a Store column, rather than 26 context switches.
 *
 * ⚠ THIS PAGE NEVER SWITCHES YOUR ACCOUNT. It used to: clicking a row set the
 * active customer to that store and navigated to the per-store invoice page.
 * That ejected the person from the chain view on their first click — they
 * landed on one store, saw its handful of invoices, and had no selection, no
 * print and no pay, with nothing on screen explaining why. Rows now SELECT,
 * which is what a checkbox column trains you to expect, and viewing an invoice
 * opens the chain-scoped PDF (the same document Print and Download emit), so
 * every action here works from the master and leaves you on it.
 *
 * ⚠ PRINTING and PAYING deliberately do NOT switch. Those are the two things
 * an AP person does in bulk, and making them switch accounts 26 times to do a
 * day's work is the whole reason this page exists — so both go through the
 * chain-scoped endpoints (customer-group-invoice-pdf / -pay-invoices) which
 * resolve each invoice's owning store server-side.
 */

type Status = 'open' | 'pastdue' | 'paid' | 'all';

const STATUSES: { id: Status; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'pastdue', label: 'Past due' },
  { id: 'paid', label: 'Paid' },
  { id: 'all', label: 'All' },
];

const ALL_STORES = '__all__';
/** Must match MAX_BATCH in customer-group-invoice-pdf. */
const MAX_PRINT = 25;

export default function ChainInvoices() {
  const { data, isLoading } = useGroupInvoices();

  const [status, setStatus] = useState<Status>('open');
  const [store, setStore] = useState<string>(ALL_STORES);
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [docBusy, setDocBusy] = useState<'print' | 'download' | string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);

  const rows = useMemo(() => {
    const all = data?.invoices ?? [];
    const q = term.trim().toLowerCase();
    return all.filter((r) => {
      if (store !== ALL_STORES && r.customer_id !== store) return false;
      if (status === 'open' && !r.is_open) return false;
      if (status === 'pastdue' && !(r.is_open && r.days_past_due > 0)) return false;
      if (status === 'paid' && r.is_open) return false;
      if (q) {
        // The PO is in the haystack deliberately: matching a payment to the
        // purchase order it settles is the whole reason an AP desk searches here.
        const hay =
          `${r.doc_number ?? ''} ${r.store_name} ${r.store_code ?? ''} ${r.po_number ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, status, store, term]);

  // Totals follow the STORE filter but not the status filter — the tiles are
  // what you're filtering against, so they must not move when you click one.
  const totals = useMemo(() => {
    const scoped = (data?.invoices ?? []).filter(
      (r) => store === ALL_STORES || r.customer_id === store,
    );
    const open = scoped.filter((r) => r.is_open);
    const past = open.filter((r) => r.days_past_due > 0);
    return {
      open: open.reduce((s, r) => s + r.balance, 0),
      openCount: open.length,
      pastDue: past.reduce((s, r) => s + r.balance, 0),
      pastDueCount: past.length,
      storeCount: new Set(open.map((r) => r.customer_id)).size,
    };
  }, [data, store]);

  // A selection must never outlive the rows it was made on: filter to Paid
  // with 12 open invoices ticked and "Pay 12" would still be armed against
  // invoices you can no longer see.
  const visibleIds = useMemo(() => rows.map((r) => r.qbo_invoice_id), [rows]);
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const keep = new Set(visibleIds.filter((id) => prev.has(id)));
      return keep.size === prev.size ? prev : keep;
    });
  }, [visibleIds]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.qbo_invoice_id)),
    [rows, selected],
  );
  // An invoice already settling is not payable — ticking it should still print
  // it, but it must never enter a charge. The server refuses it too; this just
  // stops the total on the button from promising something it won't do.
  const selectedOpen = selectedRows.filter((r) => r.is_open && !r.payment_in_flight);
  const selectedTotal = selectedOpen.reduce((s, r) => s + r.balance, 0);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(visibleIds));

  /** Print or download — one invoice or the whole selection as one document. */
  async function getPdf(ids: string[], mode: 'print' | 'download', key: string) {
    if (ids.length === 0) return;
    setDocError(null);
    setDocBusy(key);
    try {
      // runPdf opens the print tab before the fetch — see openPdfTab.
      await runPdf(
        mode,
        () => fetchFunctionPdf(`customer-group-invoice-pdf?invoice_ids=${ids.join(',')}`),
        ids.length === 1 ? `brix-invoice-${ids[0]}.pdf` : `brix-invoices-${ids.length}.pdf`,
      );
    } catch (err) {
      setDocError(err instanceof Error ? err.message : String(err));
    } finally {
      setDocBusy(null);
    }
  }

  /** View one invoice — the real branded PDF, in a new tab. No account switch. */
  async function viewInvoice(row: GroupInvoiceRow) {
    setDocError(null);
    setDocBusy(`view:${row.qbo_invoice_id}`);
    try {
      // The browser's own PDF viewer has print and save — no second set of
      // buttons to build. The tab opens at the click, before the fetch.
      await runPdf(
        'view',
        () => fetchFunctionPdf(`customer-group-invoice-pdf?invoice_ids=${row.qbo_invoice_id}`),
        `brix-invoice-${row.doc_number ?? row.qbo_invoice_id}.pdf`,
      );
    } catch (err) {
      setDocError(err instanceof Error ? err.message : String(err));
    } finally {
      setDocBusy(null);
    }
  }

  const brand = data?.master?.group_brand || data?.master?.name || 'your stores';

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-16 md:pb-10">
      <div className="px-6 md:px-8 pt-8 pb-12 max-w-6xl mx-auto">
        <h1 className="mb-1 text-center font-display text-2xl font-bold tracking-tight text-brix-ink">
          Invoices — all stores
        </h1>
        <p className="text-center text-base mb-7 text-brix-muted max-w-2xl mx-auto">
          Every open invoice across {brand}, in one place. Each store bills its own
          account — the Store column tells you which one to charge it to.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Tile label="Open balance" value={formatUsd(totals.open)} />
          <Tile
            label="Past due"
            value={formatUsd(totals.pastDue)}
            tone={totals.pastDue > 0 ? 'warning' : 'muted'}
            sub={totals.pastDueCount > 0 ? `${totals.pastDueCount} invoices` : undefined}
          />
          <Tile
            label="Open invoices"
            value={isLoading ? '—' : String(totals.openCount)}
          />
          <Tile
            label="Stores owing"
            value={isLoading ? '—' : String(totals.storeCount)}
            sub={store === ALL_STORES ? undefined : 'filtered to one store'}
          />
        </div>

        <BalanceWithCredits openBalance={totals.open} />

        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setStatus(f.id)}
                className={cn(
                  'px-5 py-2 text-sm rounded-full cursor-pointer transition-all duration-200',
                  status === f.id
                    ? 'bg-brix-primary-tint text-brix-primary font-semibold shadow-card'
                    : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <div className="relative">
              <Store size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted" />
              <select
                value={store}
                onChange={(e) => setStore(e.target.value)}
                className="pl-8 pr-3 py-2.5 rounded-xl border border-brix-border bg-white text-sm text-brix-ink max-w-[15rem]"
              >
                <option value={ALL_STORES}>All stores</option>
                {(data?.stores ?? []).map((s) => (
                  <option key={s.customer_id} value={s.customer_id}>
                    {s.store_code ? `${s.store_code} · ${s.name}` : s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted" />
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Invoice # or store…"
                className="pl-8 pr-3 py-2.5 rounded-xl border border-brix-border bg-white text-sm w-52"
              />
            </div>
            <Link
              to="/statements"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-brix-primary/40 text-brix-primary text-sm font-semibold hover:bg-brix-primary-tint/40 transition-colors whitespace-nowrap"
            >
              <ReceiptText size={15} /> Statement
            </Link>
          </div>
        </div>

        {docError && (
          <div className="mb-4 rounded-xl border border-brix-warning/30 bg-brix-warning/5 px-4 py-3 text-sm text-brix-warning">
            {docError}
          </div>
        )}

        {isLoading ? (
          <div className="py-16 text-center text-sm text-brix-muted">
            <Loader2 size={16} className="animate-spin inline-block mr-2" />
            Loading every store&rsquo;s invoices…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-brix-muted">
            Nothing here — try another status or store.
          </div>
        ) : (
          <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-brix-surface-alt/60 text-left text-[11px] uppercase tracking-wide text-brix-muted">
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = selected.size > 0 && !allVisibleSelected;
                        }}
                        onChange={toggleAll}
                        aria-label="Select all shown"
                        className="cursor-pointer"
                      />
                    </th>
                    <th className="px-4 py-3 font-semibold">Store</th>
                    <th className="px-4 py-3 font-semibold">Invoice</th>
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Due</th>
                    <th className="px-4 py-3 font-semibold text-right">Amount</th>
                    <th className="px-4 py-3 font-semibold text-right">Balance</th>
                    <th className="px-3 py-3 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.qbo_invoice_id}
                      onClick={() => toggleOne(r.qbo_invoice_id)}
                      className={cn(
                        'border-t border-brix-border/70 hover:bg-brix-primary-tint/30 cursor-pointer transition-colors',
                        selected.has(r.qbo_invoice_id) && 'bg-brix-primary-tint/25',
                      )}
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(r.qbo_invoice_id)}
                          onChange={() => toggleOne(r.qbo_invoice_id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select invoice ${r.doc_number ?? r.qbo_invoice_id}`}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="block font-medium text-brix-ink truncate max-w-[15rem]">
                          {r.store_name}
                        </span>
                        {r.store_code && (
                          <span className="block text-[11px] text-brix-muted">#{r.store_code}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => void viewInvoice(r)}
                          disabled={docBusy !== null}
                          className="text-brix-primary hover:underline disabled:opacity-50 cursor-pointer"
                          title="Open this invoice as a PDF"
                        >
                          {r.doc_number || '—'}
                        </button>
                        {docBusy === `view:${r.qbo_invoice_id}` && (
                          <Loader2 size={12} className="inline-block ml-2 animate-spin opacity-70" />
                        )}
                        {r.po_number && (
                          <span className="block text-[11px] text-brix-muted font-normal">
                            PO {r.po_number}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-brix-muted">{fmtMirrorDate(r.txn_date)}</td>
                      <td className="px-4 py-3">
                        {r.due_date ? (
                          <>
                            <span className="text-brix-muted">{fmtMirrorDate(r.due_date)}</span>
                            {r.days_past_due > 0 && (
                              <span className="ml-2 text-[11px] font-semibold text-brix-warning">
                                {r.days_past_due}d late
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-brix-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-brix-muted tabular-nums">
                        {formatUsd(r.total)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">
                        {!r.is_open ? (
                          <span className="text-brix-muted font-normal">Paid</span>
                        ) : r.payment_in_flight ? (
                          <>
                            {formatUsd(r.balance)}
                            <span className="block text-[11px] font-normal text-brix-muted">
                              Payment clearing
                            </span>
                          </>
                        ) : (
                          formatUsd(r.balance)
                        )}
                      </td>
                      <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => void getPdf([r.qbo_invoice_id], 'print', r.qbo_invoice_id)}
                          disabled={docBusy === r.qbo_invoice_id}
                          title="Print this invoice"
                          className="p-1.5 rounded-lg text-brix-muted hover:bg-brix-primary-tint hover:text-brix-ink transition-colors disabled:opacity-40"
                        >
                          {docBusy === r.qbo_invoice_id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Printer size={14} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="mt-4 text-center text-xs text-brix-muted">
          Click a row to select it, or an invoice number to open the PDF. Everything here
          — viewing, printing and paying — works from this account and leaves you on it;
          each store is still billed and paid on its own books. Open invoices are always
          shown; paid invoices are shown from {fmtMirrorDate(data?.paid_since ?? '2025-01-01')}.
        </p>
      </div>

      {selected.size > 0 && (
        <div className="sticky bottom-0 z-30 border-t border-brix-border bg-brix-surface/95 backdrop-blur px-6 md:px-8 py-3">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
            <span className="text-sm text-brix-ink">
              <span className="font-semibold">{selected.size}</span> selected
              {selectedOpen.length > 0 && (
                <span className="text-brix-muted">
                  {' '}
                  · {formatUsd(selectedTotal)} open across{' '}
                  {new Set(selectedOpen.map((r) => r.customer_id)).size} store
                  {new Set(selectedOpen.map((r) => r.customer_id)).size === 1 ? '' : 's'}
                </span>
              )}
            </span>

            <div className="flex-1" />

            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void getPdf(Array.from(selected).slice(0, MAX_PRINT), 'download', 'download')
              }
              disabled={docBusy !== null}
            >
              {docBusy === 'download' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Download
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void getPdf(Array.from(selected).slice(0, MAX_PRINT), 'print', 'print')
              }
              disabled={docBusy !== null}
            >
              {docBusy === 'print' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Printer size={14} />
              )}
              Print {selected.size > MAX_PRINT ? `first ${MAX_PRINT}` : selected.size}
            </Button>
            <Button
              size="sm"
              onClick={() => setPayOpen(true)}
              disabled={selectedOpen.length === 0 || docBusy !== null}
              title={
                selectedOpen.length === 0 ? 'Nothing selected has an open balance' : undefined
              }
            >
              <CreditCard size={14} />
              Pay {formatUsd(selectedTotal)}
            </Button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="p-1.5 rounded-lg text-brix-muted hover:bg-brix-primary-tint hover:text-brix-ink transition-colors"
              aria-label="Clear selection"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {payOpen && (
        <ChainPayModal
          invoiceIds={selectedOpen.map((r) => r.qbo_invoice_id)}
          onClose={() => {
            setPayOpen(false);
            // A run that paid anything leaves a stale tick list behind; the
            // refetched rows are the truth about what is still open.
            setSelected(new Set());
          }}
        />
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'warning' | 'muted';
}) {
  return (
    <div className="rounded-2xl border border-brix-border bg-white px-4 py-3.5">
      <p className="text-[11px] uppercase tracking-wide text-brix-muted font-semibold">{label}</p>
      <p
        className={cn(
          'mt-1 font-display text-xl font-bold tabular-nums',
          tone === 'warning' ? 'text-brix-warning' : 'text-brix-ink',
        )}
      >
        {value}
      </p>
      {sub && <p className="text-[11px] text-brix-muted mt-0.5">{sub}</p>}
    </div>
  );
}
